/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpExportDocument.tsx
 * FILE_ID: JOBHUB-QA-ITP-EXPORT-DOCUMENT-001
 * PURPOSE: Live ITP preview that can be edited in place.
 * WHY: srcDoc is only rewritten when seedKey changes, so typing does not jump the caret.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { ContextMenu, useContextMenu, type ContextMenuItem } from '@quotech/ui'
import {
  insertParticipantRow,
  participantTargetAt,
  placeCaret,
  removeParticipantRow,
  type ParticipantTarget,
} from './participantRows'
import {
  attachPreviewRemoveButtons,
  confirmRemoveImage,
  removePreviewCheckPhoto,
  savedPreviewHtml,
} from './itpPreviewImageRemove'

export function QaItpExportDocument({
  seedHtml,
  seedKey,
  livePatch = null,
  onChange,
  touch = false,
  onRemoveCover,
  onHoldSign,
}: {
  seedHtml: string
  seedKey: number
  livePatch?: string | null
  onChange: (html: string) => void
  touch?: boolean
  onRemoveCover: () => Promise<boolean>
  onHoldSign?: (itemId: string, areaId: string) => void
}) {
  const timer = useRef<number | null>(null)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const docRef = useRef<Document | null>(null)
  const openedAt = useRef(0)
  const userEdited = useRef(false)
  const menu = useContextMenu()
  const [target, setTarget] = useState<ParticipantTarget | null>(null)
  const touchRef = useRef(touch)
  touchRef.current = touch
  const onRemoveCoverRef = useRef(onRemoveCover)
  onRemoveCoverRef.current = onRemoveCover
  const onHoldSignRef = useRef(onHoldSign)
  onHoldSignRef.current = onHoldSign
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const emit = useCallback(() => {
    const doc = docRef.current
    if (!doc) return
    if (timer.current != null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      const live = docRef.current
      if (!live) return
      onChangeRef.current(savedPreviewHtml(live))
    }, 250)
  }, [])

  useEffect(() => {
    const doc = docRef.current
    if (!doc || !livePatch) return
    const parsed = new DOMParser().parseFromString(livePatch, 'text/html')
    const next = parsed.querySelector('.itp-cover-photo')
    const current = doc.querySelector('.itp-cover-photo')
    if (next) {
      const imported = doc.importNode(next, true)
      if (current) current.replaceWith(imported)
      else doc.querySelector('.itp-cover-title')?.insertAdjacentElement('afterend', imported)
    } else current?.remove()
    doc.querySelectorAll('.itp-preview-shot').forEach((shot) => {
      if (!shot.querySelector('img')) shot.remove()
    })
    const title = parsed.querySelector('.itp-cover-title')
    const liveTitle = doc.querySelector('.itp-cover-title')
    if (title && liveTitle) liveTitle.textContent = title.textContent
    const hide = parsed.body.getAttribute('data-itp-hide')
    if (hide != null) doc.body.setAttribute('data-itp-hide', hide)
    attachPreviewRemoveButtons(doc, touchRef.current)
    emit()
  }, [livePatch, emit])

  const openAt = (node: EventTarget | null, x: number, y: number) => {
    const next = participantTargetAt(node)
    if (!next) return
    if (Date.now() - openedAt.current < 700) return
    openedAt.current = Date.now()
    const rect = frameRef.current?.getBoundingClientRect()
    setTarget(next)
    menu.openAt({ clientX: (rect?.left ?? 0) + x, clientY: (rect?.top ?? 0) + y })
  }

  const run = (action: () => HTMLTableCellElement | void) => {
    const cell = action()
    if (cell) placeCaret(cell)
    emit()
    menu.close()
    setTarget(null)
  }

  const items: ContextMenuItem[] = target?.kind === 'row'
    ? [
      { id: 'above', label: 'Insert contact row above', icon: Plus, onSelect: () => run(() => insertParticipantRow(target, 'above')) },
      { id: 'below', label: 'Insert contact row below', icon: Plus, onSelect: () => run(() => insertParticipantRow(target, 'below')) },
      { id: 'sep', kind: 'separator' },
      { id: 'remove', kind: 'destructive', label: 'Remove contact row', icon: Trash2, onSelect: () => run(() => removeParticipantRow(target.row)) },
    ]
    : [
      {
        id: 'insert',
        label: 'Insert contact row',
        icon: Plus,
        onSelect: () => {
          if (target) run(() => insertParticipantRow(target, 'below'))
        },
      },
    ]

  return (
    <>
      <iframe
        ref={frameRef}
        key={seedKey}
        title="ITP document"
        srcDoc={seedHtml}
        className="block h-full w-full border-0 bg-white"
        onLoad={(event) => {
          const doc = event.currentTarget.contentDocument
          if (!doc) return
          docRef.current = doc
          doc.designMode = 'on'
          userEdited.current = false
          attachPreviewRemoveButtons(doc, touchRef.current)
          doc.addEventListener('pointerdown', (event) => {
            const hold = (event.target as Element | null)?.closest?.('.itp-hold-sign')
            if (hold instanceof HTMLButtonElement) {
              event.preventDefault()
              event.stopImmediatePropagation()
              const article = hold.closest('article')
              const itemId = article?.getAttribute('data-itp-item') ?? ''
              const areaId = article?.getAttribute('data-itp-area') ?? ''
              if (itemId) onHoldSignRef.current?.(itemId, areaId)
              return
            }
            const button = (event.target as Element | null)?.closest?.('.itp-image-remove')
            if (!(button instanceof HTMLButtonElement)) return
            event.preventDefault()
            event.stopImmediatePropagation()
            const kind = button.dataset.kind === 'check' ? 'check' : 'cover'
            if (!confirmRemoveImage(kind)) return
            void (async () => {
              if (kind === 'cover') {
                if (!await onRemoveCoverRef.current()) return
                button.closest('.itp-preview-shot')?.remove()
              } else {
                const img = button.closest('.frame')?.querySelector('img')
                  ?? button.parentElement?.querySelector('img')
                if (img instanceof HTMLImageElement) removePreviewCheckPhoto(img)
              }
              attachPreviewRemoveButtons(doc, touchRef.current)
              emit()
            })()
          }, true)
          const markEdited = () => { userEdited.current = true }
          doc.addEventListener('keydown', markEdited)
          doc.addEventListener('pointerdown', markEdited)
          doc.addEventListener('input', () => {
            if (!userEdited.current) return
            emit()
          })
          doc.addEventListener('contextmenu', (event) => {
            if (!participantTargetAt(event.target)) return
            event.preventDefault()
            openAt(event.target, event.clientX, event.clientY)
          })
          let press: { x: number; y: number; timer: number } | null = null
          doc.addEventListener('touchstart', (event) => {
            const touch = event.changedTouches[0]
            if (!touch || !participantTargetAt(event.target)) return
            press = {
              x: touch.clientX,
              y: touch.clientY,
              timer: window.setTimeout(() => openAt(event.target, touch.clientX, touch.clientY), 550),
            }
          }, { passive: true })
          doc.addEventListener('touchmove', (event) => {
            const touch = event.changedTouches[0]
            if (!press || !touch) return
            if (Math.hypot(touch.clientX - press.x, touch.clientY - press.y) > 10) {
              window.clearTimeout(press.timer)
              press = null
            }
          }, { passive: true })
          const cancel = () => {
            if (!press) return
            window.clearTimeout(press.timer)
            press = null
          }
          doc.addEventListener('touchend', cancel, { passive: true })
          doc.addEventListener('touchcancel', cancel, { passive: true })
        }}
      />
      {target && (
        <ContextMenu
          {...menu.props}
          onClose={() => { menu.close(); setTarget(null) }}
          width={220}
          items={items}
        />
      )}
    </>
  )
}
